import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { 
  ShieldCheckIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  SparklesIcon,
  GlobeAltIcon,
  HeartIcon,
  PlayIcon,
  ChevronRightIcon,
  CheckIcon,
  LightBulbIcon,
  CpuChipIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline'
import { 
  StarIcon as StarSolidIcon
} from '@heroicons/react/24/solid'
import { useAuthStore } from '../stores/authStore'
import { useLanguageStore } from '../stores/languageStore'
import { useCaseTextStore } from '../stores/caseTextStore'

const HomePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { language, isRTL } = useLanguageStore()
  const { setCaseText } = useCaseTextStore()
  const [activeDemo, setActiveDemo] = useState(0)
  const [currentTestimonial, setCurrentTestimonial] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [caseDescription, setCaseDescription] = useState('')

  // Auto-rotate demo every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveDemo((prev) => (prev + 1) % 4)
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  // Auto-rotate testimonials every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % 3)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  // Trigger animations on load
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100)
  }, [])

  // Handle starting a case
  const handleStartCase = () => {
    if (caseDescription.trim()) {
      setCaseText(caseDescription.trim())
    }
    
    if (isAuthenticated) {
      navigate('/file-lawsuit')
    } else {
      navigate('/register')
    }
  }

  // Helper function to get language-specific image URL with fallback to English
  const getImageUrl = (baseName: string) => {
    // For now, we have images for English only, so we'll use 'en' as default
    // In the future, this can be expanded to check if the image exists for the current language
    const currentLanguage = language || 'en'
    
    // List of available languages (currently only English)
    const availableLanguages = ['en']
    
    // Use current language if available, otherwise fallback to English
    const languageToUse = availableLanguages.includes(currentLanguage) ? currentLanguage : 'en'
    
    // Special cases for files that already have language suffix
    const filesWithoutLanguageSuffix = ['generate_docs_eng', 'review_docs_eng']
    if (filesWithoutLanguageSuffix.includes(baseName)) {
      return `https://storage.googleapis.com/tvia-ai-shared/screenshots/${baseName}.jpg`
    }
    
    return `https://storage.googleapis.com/tvia-ai-shared/screenshots/${baseName}_${languageToUse}.jpg`
  }

  const features = [
    {
      icon: SparklesIcon,
      title: t('home.features.aiAnalysis.title'),
      description: t('home.features.aiAnalysis.description'),
      image: getImageUrl('doc_generation_loading'),
      gradient: 'from-purple-500 to-pink-500',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      icon: GlobeAltIcon,
      title: t('home.features.multiLanguage.title'),
      description: t('home.features.multiLanguage.description'),
      image: getImageUrl('dashboard'),
      gradient: 'from-blue-500 to-cyan-500',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      icon: UserGroupIcon,
      title: t('home.features.classActions.title'),
      description: t('home.features.classActions.description'),
      image: getImageUrl('class_action'),
      gradient: 'from-green-500 to-emerald-500',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      icon: ShieldCheckIcon,
      title: t('home.features.globalCoverage.title'),
      description: t('home.features.globalCoverage.description'),
      image: getImageUrl('verification'),
      gradient: 'from-orange-500 to-red-500',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    },
  ]

  const steps = [
    {
      number: '01',
      title: t('home.howItWorks.step1.title'),
      description: t('home.howItWorks.step1.description'),
      icon: DocumentTextIcon,
      image: getImageUrl('file_lawsuit'),
      demo: 'Upload documents and describe your case in your native language',
      color: 'blue'
    },
    {
      number: '02',
      title: t('home.howItWorks.step2.title'),
      description: t('home.howItWorks.step2.description'),
      icon: SparklesIcon,
      image: getImageUrl('generate_docs_eng'),
      demo: 'AI analyzes your case and generates legal documents',
      color: 'purple'
    },
    {
      number: '03',
      title: t('home.howItWorks.step3.title'),
      description: t('home.howItWorks.step3.description'),
      icon: DocumentTextIcon,
      image: getImageUrl('review_docs_eng'),
      demo: 'Review and customize generated legal documents',
      color: 'green'
    },
    {
      number: '04',
      title: t('home.howItWorks.step4.title'),
      description: t('home.howItWorks.step4.description'),
      icon: EnvelopeIcon,
      image: getImageUrl('edit_lawsuit'),
      demo: 'Submit electronically or mail physical documents (optional fee)',
      color: 'orange'
    },
  ]

  const stats = [
    { 
      number: '100%', 
      label: t('home.stats.free'), 
      highlight: true,
      icon: HeartIcon,
      description: t('home.stats.freeNote', 'Always Free')
    },
    { 
      number: '1,000+', 
      label: t('home.stats.lawsuitsFiled'),
      icon: DocumentTextIcon,
      description: 'Growing daily'
    },
    { 
      number: '250+', 
      label: t('home.stats.classActions'),
      icon: UserGroupIcon,
      description: 'Collective power'
    },
    { 
      number: '5', 
      label: t('home.stats.languages'),
      icon: GlobeAltIcon,
      description: 'Global access'
    },
  ]

  const testimonials = [
    {
      quote: t('home.testimonials.testimonial1.quote'),
      author: t('home.testimonials.testimonial1.author'),
      role: t('home.testimonials.testimonial1.role'),
      avatar: '/images/avatars/user1.jpg',
      rating: 5,
      location: 'New York, USA'
    },
    {
      quote: t('home.testimonials.testimonial2.quote'),
      author: t('home.testimonials.testimonial2.author'),
      role: t('home.testimonials.testimonial2.role'),
      avatar: '/images/avatars/user2.jpg',
      rating: 5,
      location: 'London, UK'
    },
    {
      quote: t('home.testimonials.testimonial3.quote'),
      author: t('home.testimonials.testimonial3.author'),
      role: t('home.testimonials.testimonial3.role'),
      avatar: '/images/avatars/user3.jpg',
      rating: 5,
      location: 'Berlin, Germany'
    }
  ]

  const freeFeatures = [
    { icon: CpuChipIcon, text: t('home.freeFeatures.aiPowered'), gradient: 'from-purple-500 to-pink-500' },
    { icon: GlobeAltIcon, text: t('home.freeFeatures.multiLanguage'), gradient: 'from-blue-500 to-cyan-500' },
    { icon: DocumentTextIcon, text: t('home.freeFeatures.documentGeneration'), gradient: 'from-green-500 to-emerald-500' },
    { icon: UserGroupIcon, text: t('home.freeFeatures.classActions'), gradient: 'from-orange-500 to-red-500' },
    { icon: LightBulbIcon, text: t('home.freeFeatures.legalGuidance'), gradient: 'from-yellow-500 to-orange-500' },
    { icon: ShieldCheckIcon, text: t('home.freeFeatures.secureHandling'), gradient: 'from-indigo-500 to-purple-500' }
  ]



  const coreFeatures = [
    {
      icon: SparklesIcon,
      title: t('home.features.aiAnalysis.title', 'AI-Powered Analysis'),
      description: t('home.features.aiAnalysis.description', 'Smart AI analyzes your case and generates legal documents')
    },
    {
      icon: UserGroupIcon,
      title: t('home.features.classActions.title', 'Join Class Actions'),
      description: t('home.features.classActions.description', 'Connect with others who have similar cases')
    },
    {
      icon: ShieldCheckIcon,
      title: t('home.features.globalCoverage.title', 'Always Free'),
      description: t('home.features.globalCoverage.description', 'Complete platform access with no hidden fees')
    }
  ]

  return (
    <>
      <Helmet>
        <title>{t('seo.homeTitle')}</title>
        <meta name="description" content={t('seo.homeDescription')} />
        <meta name="keywords" content={t('seo.homeKeywords')} />
        <meta property="og:title" content={t('seo.homeTitle')} />
        <meta property="og:description" content={t('seo.homeDescription')} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('seo.homeTitle')} />
        <meta name="twitter:description" content={t('seo.homeDescription')} />
        <link rel="canonical" href={window.location.origin} />
        
        {/* Alternate language URLs */}
        <link rel="alternate" hrefLang="en" href={`${window.location.origin}/en`} />
        <link rel="alternate" hrefLang="he" href={`${window.location.origin}/he`} />
        <link rel="alternate" hrefLang="ar" href={`${window.location.origin}/ar`} />
        <link rel="alternate" hrefLang="de" href={`${window.location.origin}/de`} />
        <link rel="alternate" hrefLang="fr" href={`${window.location.origin}/fr`} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": t('home.brandName'),
            "description": t('home.description'),
            "url": window.location.origin,
            "logo": `${window.location.origin}/logo.png`,
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Service",
              "availableLanguage": ["en", "he", "ar", "de", "fr"]
            },
            "service": {
              "@type": "LegalService",
              "name": t('home.serviceName'),
              "description": t('home.description'),
              "provider": {
                "@type": "Organization",
                "name": t('home.brandName')
              }
            }
          })}
        </script>
      </Helmet>

      <div className={`bg-white overflow-hidden ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Simplified Hero Section */}
        <div id="hero-section" className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 min-h-screen flex items-center">
          {/* Subtle Background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-10"></div>
            <div className="absolute bottom-20 right-10 w-72 h-72 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-10"></div>
          </div>
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
            <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              
              {/* Free Badge */}
              <div className={`inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full text-sm font-bold mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <HeartIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('home.freeBadge', '100% Free Forever')}
              </div>
              
              <h1 className="text-6xl md:text-8xl font-bold text-gray-900 mb-8 leading-tight">
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
                  {t('home.heroTitle', 'Get Justice')}
                </span>
              </h1>
              
              <p className="text-2xl md:text-3xl text-gray-600 mb-12 leading-relaxed max-w-4xl mx-auto">
                {t('home.heroSubtitle', 'AI-powered legal platform that helps you file lawsuits and join class actions - completely free')}
              </p>

              {/* Main CTA with Text Area */}
              <div className="mb-16 max-w-3xl mx-auto">
                <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                    {t('home.tellUsAboutCase', 'Tell us about your case')}
                  </h2>
                  
                  <textarea
                    value={caseDescription}
                    onChange={(e) => setCaseDescription(e.target.value)}
                    placeholder={t('home.caseDescriptionPlaceholder', 'Describe what happened, who is involved, and what you\'re seeking...')}
                    className={`w-full h-32 p-4 border-2 border-gray-200 rounded-2xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all resize-none text-lg ${isRTL ? 'text-right placeholder:text-right' : 'text-left placeholder:text-left'}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                  
                  <div className="mt-6 text-center">
                    <button
                      onClick={handleStartCase}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-12 py-4 rounded-2xl text-xl font-bold transition-all transform hover:scale-105 inline-flex items-center justify-center group shadow-xl hover:shadow-2xl"
                    >
                      <RocketLaunchIcon className={`${isRTL ? 'ml-4' : 'mr-4'} h-6 w-6 group-hover:animate-bounce`} />
                      {t('home.getJustice', 'Get Justice')}
                      <ArrowRightIcon className={`${isRTL ? 'mr-4' : 'ml-4'} h-6 w-6 group-hover:${isRTL ? '-translate-x-2' : 'translate-x-2'} transition-transform`} />
                    </button>
                  </div>
                  

                </div>
              </div>

              {/* Secondary Actions */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
                <Link
                  to="/legal-documents"
                  className="border-2 border-purple-300 hover:border-purple-500 hover:bg-purple-50 text-purple-700 px-8 py-4 rounded-2xl text-lg font-semibold transition-all inline-flex items-center justify-center group"
                >
                  <DocumentTextIcon className={`${isRTL ? 'ml-3' : 'mr-3'} h-6 w-6`} />
                  {t('home.generateLegalDocs', 'Generate Legal Docs')}
                </Link>
                
                <button 
                  className="border-2 border-gray-300 hover:border-gray-500 hover:bg-gray-50 text-gray-700 px-8 py-4 rounded-2xl text-lg font-semibold transition-all inline-flex items-center justify-center group"
                  onClick={() => document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <PlayIcon className={`${isRTL ? 'ml-3' : 'mr-3'} h-6 w-6`} />
                  {t('home.howItWorks.title', 'How It Works')}
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                {coreFeatures.map((feature, index) => (
                  <div key={index} className="text-center">
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                      <div className="flex justify-center mb-4">
                        <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-full p-3">
                          <feature.icon className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <h3 className="font-bold text-gray-900 text-lg mb-2">{feature.title}</h3>
                      <p className="text-gray-600 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* What's Completely Free Section */}
        <div className="py-24 bg-gradient-to-br from-green-50 to-emerald-50 relative overflow-hidden">
          {/* Background Decoration */}
          <div className="absolute inset-0">
            <div className="absolute top-10 left-10 w-32 h-32 bg-green-200 rounded-full opacity-20 animate-float"></div>
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-emerald-200 rounded-full opacity-20 animate-float delay-1000"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-16">
              <div className={`inline-flex items-center bg-green-500 text-white px-6 py-3 rounded-full text-sm font-bold mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CheckIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                100% FREE FOREVER
                <StarSolidIcon className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'} text-yellow-300`} />
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  {t('home.freeSection.title', 'Everything You Need - Completely Free')}
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                {t('home.freeSection.subtitle', 'No hidden fees, no subscriptions, no premium tiers. Justice should be accessible to everyone.')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {freeFeatures.map((feature, index) => (
                <div key={index} className={`bg-white rounded-2xl p-8 shadow-lg border-2 border-green-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group animate-fade-in-up`} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-4 flex-row-reverse' : 'space-x-4'}`}>
                    <div className={`bg-gradient-to-r ${feature.gradient} rounded-xl p-3 group-hover:scale-110 transition-transform duration-300`}>
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-lg mb-2">{feature.text}</p>
                      <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <CheckIcon className={`h-5 w-5 text-green-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <span className="text-green-600 font-medium">Included Free</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-16">
              <div className="bg-white rounded-3xl p-10 shadow-2xl border-4 border-green-200 max-w-3xl mx-auto transform hover:scale-105 transition-transform duration-300">
                <h3 className={`text-2xl font-bold text-gray-900 mb-6 flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <EnvelopeIcon className={`h-8 w-8 text-blue-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {t('home.onlyCharge.title', 'The Only Optional Costs')}
                </h3>
                <div className="space-y-4">
                  <div className={`flex items-center justify-between p-4 bg-gray-50 rounded-xl ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
                      <div className="bg-blue-100 rounded-lg p-2">
                        <PaperAirplaneIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <span className="text-gray-800 font-medium">{t('home.onlyCharge.filing', 'Lawsuit Filing & Service')}</span>
                    </div>
                    <span className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full">{t('home.onlyCharge.filingPrice', 'Actual court costs only')}</span>
                  </div>
                  <div className={`flex items-center justify-between p-4 bg-gray-50 rounded-xl ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
                      <div className="bg-blue-100 rounded-lg p-2">
                        <EnvelopeIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <span className="text-gray-800 font-medium">{t('home.onlyCharge.mailing', 'Document Mailing Service')}</span>
                    </div>
                    <span className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full">{t('home.onlyCharge.mailingPrice', 'Starting from $15')}</span>
                  </div>
                  <div className={`flex items-center justify-between p-4 bg-gray-50 rounded-xl ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
                       <div className="bg-blue-100 rounded-lg p-2">
                         <SparklesIcon className="h-6 w-6 text-blue-600" />
                       </div>
                       <span className="text-gray-800 font-medium">{t('home.onlyCharge.premiumAI', 'Premium AI Models')}</span>
                     </div>
                     <span className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full">{t('home.onlyCharge.premiumAIPrice', 'Optional upgrade')}</span>
                   </div>
                 </div>
                <p className="text-green-600 font-bold mt-6 text-lg">
                  ✨ Everything else is completely FREE forever! ✨
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Demo Section */}
        <div id="demo-section" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className={`inline-flex items-center bg-blue-500 text-white px-6 py-3 rounded-full text-sm font-bold mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <PlayIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                LIVE DEMO
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {t('home.demo.title', 'See Our Platform in Action')}
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('home.demo.subtitle', 'Watch how easy it is to get justice with our AI-powered platform')}
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Demo Controls */}
              <div className="space-y-4">
                {steps.map((step, index) => (
                  <div 
                    key={index}
                    className={`cursor-pointer transition-all duration-500 transform ${
                      activeDemo === index 
                        ? `bg-gradient-to-r from-${step.color}-50 to-${step.color}-100 border-${step.color}-300 shadow-lg scale-105` 
                        : 'bg-white border-gray-200 hover:shadow-md hover:scale-102'
                    } rounded-2xl border-2 p-8 group`}
                    onClick={() => setActiveDemo(index)}
                  >
                    <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-6 flex-row-reverse' : 'space-x-6'}`}>
                      <div className={`rounded-2xl w-16 h-16 flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                        activeDemo === index 
                          ? `bg-gradient-to-r from-${step.color}-500 to-${step.color}-600 text-white shadow-lg scale-110` 
                          : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                      }`}>
                        {step.number}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-xl mb-3">{step.title}</h3>
                        <p className="text-gray-600 mb-3 leading-relaxed">{step.description}</p>
                        <p className={`text-sm font-medium ${
                          activeDemo === index ? `text-${step.color}-600` : 'text-blue-600'
                        }`}>
                          {step.demo}
                        </p>
                      </div>
                      <ChevronRightIcon className={`h-6 w-6 transition-all duration-300 ${
                        activeDemo === index ? `text-${step.color}-600 rotate-90` : 'text-gray-400 group-hover:text-gray-600'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Demo Visual */}
              <div className="relative">
                <div className="bg-white rounded-3xl p-6 shadow-2xl border-2 border-gray-100">
                  <div className="bg-white rounded-2xl h-96 relative overflow-hidden border border-gray-200">
                    {/* Browser UI Header */}
                    <div className="absolute top-0 left-0 right-0 bg-gray-100 border-b border-gray-200 px-4 py-3 z-10 rounded-t-2xl">
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'}`}>
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <div className="flex-1 bg-white rounded px-3 py-1 mx-4">
                          <div className="text-xs text-gray-500">tvia.ai/{steps[activeDemo].number.toLowerCase()}</div>
                        </div>
                      </div>
                    </div>

                    {/* Dynamic Content Based on Active Step */}
                    <div className="pt-12 h-full">
                      <img 
                        src={steps[activeDemo].image} 
                        alt={steps[activeDemo].title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      
                      {/* Fallback content if image fails */}
                      <div className="absolute inset-0 flex items-center justify-center text-center bg-gray-50 pt-12" style={{ display: 'none' }}>
                        <div>
                          <div className={`bg-gradient-to-r from-${steps[activeDemo].color}-500 to-${steps[activeDemo].color}-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 animate-pulse`}>
                            {React.createElement(steps[activeDemo].icon, { className: "h-10 w-10 text-white" })}
                          </div>
                          <p className="text-xl font-bold mb-3 text-gray-900">
                            {steps[activeDemo].title}
                          </p>
                          <p className="text-gray-600 text-sm leading-relaxed max-w-sm mx-auto">
                            {t('home.demo.placeholder', 'Interactive demo will be displayed here')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Image Overlay with Step Info */}
                    <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 border border-gray-200">
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
                        <div className={`bg-gradient-to-r from-${steps[activeDemo].color}-500 to-${steps[activeDemo].color}-600 rounded-lg w-10 h-10 flex items-center justify-center text-white font-bold`}>
                          {steps[activeDemo].number}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-sm">{steps[activeDemo].title}</p>
                          <p className="text-xs text-gray-600">{steps[activeDemo].demo}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Progress Indicator */}
                <div className={`mt-6 flex justify-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                  {steps.map((_, index) => (
                    <button
                      key={index}
                      className={`w-4 h-4 rounded-full transition-all duration-300 ${
                        activeDemo === index 
                          ? `bg-gradient-to-r from-${steps[index].color}-500 to-${steps[index].color}-600 scale-125` 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      onClick={() => setActiveDemo(index)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {t('home.features.title')}
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('home.features.subtitle', 'Powerful tools designed to democratize access to justice')}
              </p>
            </div>
            
            <div className="space-y-24">
              {features.map((feature, index) => (
                <div key={index} className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                  <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''} animate-fade-in-up`} style={{ animationDelay: `${index * 0.2}s` }}>
                    <div className="mb-8">
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4 flex-row-reverse' : 'space-x-4'} mb-6`}>
                        <div className={`${feature.iconBg} rounded-2xl w-16 h-16 flex items-center justify-center`}>
                          <feature.icon className={`h-8 w-8 ${feature.iconColor}`} />
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900">{feature.title}</h3>
                      </div>
                      <p className="text-gray-600 text-xl leading-relaxed mb-6">
                        {feature.description}
                      </p>
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4 flex-row-reverse' : 'space-x-4'}`}>
                        <div className={`bg-gradient-to-r ${feature.gradient} text-white px-6 py-3 rounded-full text-sm font-bold`}>
                          ✨ Completely Free
                        </div>
                        <div className={`flex items-center text-gray-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <StarSolidIcon className={`h-5 w-5 text-yellow-500 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                          <span className="text-sm">Loved by thousands</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''} relative group`}>
                    <div className={`bg-gradient-to-r ${feature.gradient} rounded-3xl p-8 shadow-2xl transform group-hover:scale-105 transition-all duration-500`}>
                      <div className="bg-white rounded-2xl h-80 relative overflow-hidden">
                        <img 
                          src={feature.image} 
                          alt={feature.title}
                          className="w-full h-full object-cover rounded-2xl"
                          onError={(e) => {
                            // Fallback to placeholder if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        
                        {/* Fallback content if image fails */}
                        <div className="absolute inset-0 flex items-center justify-center text-center" style={{ display: 'none' }}>
                          <div>
                            <div className={`bg-gradient-to-r ${feature.gradient} rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4`}>
                              <feature.icon className="h-8 w-8 text-white" />
                            </div>
                            <p className="text-gray-600 font-medium text-lg">{feature.title}</p>
                            <p className="text-sm text-gray-500 mt-2">Interactive Demo Available</p>
                          </div>
                        </div>
                        
                        {/* Floating UI Elements */}
                        <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-bounce">
                          FREE
                        </div>
                      </div>
                    </div>
                    
                    {/* Decorative Elements */}
                    <div className={`absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r ${feature.gradient} rounded-full animate-ping`}></div>
                    <div className={`absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-r ${feature.gradient} rounded-full animate-pulse`}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="py-24 bg-gradient-to-br from-purple-50 to-pink-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {t('home.testimonials.title')}
                </span>
              </h2>
              <p className="text-xl text-gray-600">Real stories from our amazing community</p>
            </div>
            
            {/* Carousel Testimonials */}
            <div className="relative max-w-4xl mx-auto">
              <div className="bg-white rounded-3xl p-12 shadow-2xl border border-purple-100 min-h-80">
                <div className="text-center">
                  {/* Stars */}
                  <div className={`flex justify-center ${isRTL ? 'space-x-reverse space-x-1' : 'space-x-1'} mb-6`}>
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <StarSolidIcon key={i} className="h-6 w-6 text-yellow-500" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <blockquote className="text-2xl text-gray-800 font-medium mb-8 leading-relaxed italic">
                    "{testimonials[currentTestimonial].quote}"
                  </blockquote>
                  
                  {/* Author */}
                  <div className={`flex items-center justify-center ${isRTL ? 'space-x-reverse space-x-4 flex-row-reverse' : 'space-x-4'}`}>
                    <div className="bg-gradient-to-r from-purple-400 to-pink-400 rounded-full w-16 h-16 flex items-center justify-center text-white font-bold text-xl">
                      {testimonials[currentTestimonial].author.charAt(0)}
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <p className="font-bold text-gray-900 text-lg">{testimonials[currentTestimonial].author}</p>
                      <p className="text-gray-600">{testimonials[currentTestimonial].role}</p>
                      <p className="text-sm text-gray-500">{testimonials[currentTestimonial].location}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Navigation Dots */}
              <div className={`flex justify-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} mt-8`}>
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`w-4 h-4 rounded-full transition-all duration-300 ${
                      currentTestimonial === index 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 scale-125' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    onClick={() => setCurrentTestimonial(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 relative overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 animate-pulse"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                {t('home.stats.title')}
              </h2>
              <p className="text-blue-100 text-xl">Making justice accessible worldwide</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 transform group-hover:scale-110 transition-all duration-300">
                    <div className="flex justify-center mb-4">
                      <div className={`rounded-full p-3 ${stat.highlight ? 'bg-green-500' : 'bg-white/20'}`}>
                        <stat.icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div className={`text-4xl md:text-5xl font-bold mb-2 ${
                      stat.highlight ? 'text-green-300' : 'text-white'
                    }`}>
                      {stat.number}
                    </div>
                    <div className="text-blue-100 text-lg font-medium mb-1">
                      {stat.label}
                    </div>
                    <div className="text-blue-200 text-sm">
                      {stat.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Final CTA Section */}
        <div className="py-24 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 rounded-3xl p-16 shadow-2xl relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-white/10 bg-grid-pattern"></div>
              
              <div className="relative z-10">
                <div className="flex justify-center mb-6">
                  <div className="bg-white/20 rounded-full p-4">
                    <RocketLaunchIcon className="h-12 w-12 text-white" />
                  </div>
                </div>
                
                <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
                  {t('home.cta.title')}
                </h2>
                <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed">
                  {t('home.cta.subtitle')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-6 justify-center mb-8">
                  <button
                    onClick={() => document.getElementById('hero-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className="bg-white text-blue-600 px-10 py-5 rounded-2xl text-lg font-bold transition-all transform hover:scale-105 inline-flex items-center justify-center group shadow-xl hover:shadow-2xl"
                  >
                    <StarSolidIcon className={`${isRTL ? 'ml-3' : 'mr-3'} h-6 w-6 text-yellow-500`} />
                    {t('home.cta.getJustice', 'Get Justice Now')}
                    <ArrowRightIcon className={`${isRTL ? 'mr-3' : 'ml-3'} h-6 w-6 group-hover:${isRTL ? '-translate-x-2' : 'translate-x-2'} transition-transform`} />
                  </button>
                  
                  <Link
                    to="/about"
                    className="border-2 border-white/50 hover:border-white text-white hover:bg-white/10 px-10 py-5 rounded-2xl text-lg font-bold transition-all inline-flex items-center justify-center backdrop-blur-sm"
                  >
                    <HeartIcon className={`${isRTL ? 'ml-3' : 'mr-3'} h-6 w-6`} />
                    {t('home.cta.learnMore', 'Learn About Our Mission')}
                  </Link>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-2xl mx-auto">
                  <p className="text-white font-medium text-lg">
                    {t('home.cta.disclaimer', 'No credit card required • No hidden fees • Always free platform')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default HomePage 